import {
  getIssuerBankAccountForCurrency,
  issuerBankAccountHasAnyValue,
} from "@/modules/quotation-invoice/domain/issuer-bank-accounts";
import {
  DEFAULT_ISSUER_COMPANY_ADDRESS,
  DEFAULT_ISSUER_COMPANY_CONTACT,
  DEFAULT_ISSUER_COMPANY_NAME,
} from "@/modules/quotation-invoice/domain/issuer-defaults";
import type {
  QuotationInvoiceDocumentItemDTO,
  QuotationInvoiceDocumentListItem,
  QuotationInvoicePaymentAttachmentDTO,
} from "@/modules/quotation-invoice/domain/types";
import { config } from "@/shared/lib/config";
import { formatDateLocal } from "@/shared/lib/date-time";
import { useQuotationInvoiceIssuerSettingsQuery } from "../api/queries";
import { formatDocumentCurrency } from "../lib/format-document-currency";
import { IssuerDocumentAssetImg } from "./IssuerDocumentAssetImg";

function fileUrl(fileKey: string): string {
  return `${config.apiUrl}/files/${fileKey}`;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  bank_transfer: "ໂອນເງິນ",
  cash: "ເງິນສົດ",
  card: "ບັດ / QR",
  cheque: "ເຊັກ",
  other: "ອື່ນ",
};

function paymentMethodLabel(method: string | null | undefined): string {
  if (!method?.trim()) return "—";
  return PAYMENT_METHOD_LABELS[method.trim().toLowerCase()] ?? method;
}

export type ReceiptPaymentRecord = {
  amount: number;
  paidAt: string | null;
  method: string | null;
  notes: string | null;
  previouslyPaid: number;
  /** ยอดรวมเต็มของ invoice (ใช้แสดงแทน doc.total ใน summary) */
  invoiceTotal: number;
  /** subtotal เต็มของ invoice (ใช้แสดงแทน doc.subtotal) */
  invoiceSubtotal: number;
  /** ส่วนลดเต็มของ invoice */
  invoiceDiscountAmount: number;
  /** VAT เต็มของ invoice */
  invoiceTaxAmount: number;
  remaining: number;
  attachments: QuotationInvoicePaymentAttachmentDTO[];
};

const getDocumentTypeLabel = (type: string, typeName?: string | null) => {
  if (typeName?.trim()) return typeName;
  const map: Record<string, string> = {
    quotation: "ລາຄາ",
    invoice: "ໃບບິນ",
    receipt: "ໃບຮັບເງິນ",
  };
  return map[type] ?? type;
};

type QuotationInvoiceDocumentBodyProps = {
  document: QuotationInvoiceDocumentListItem;
  paymentRecord?: ReceiptPaymentRecord | null;
  items: QuotationInvoiceDocumentItemDTO[];
};

/**
 * Shared document layout: same markup for detail view card and PDF/print preview.
 */
export function QuotationInvoiceDocumentBody({
  document: doc,
  items,
  paymentRecord,
}: QuotationInvoiceDocumentBodyProps) {
  const issuerQuery = useQuotationInvoiceIssuerSettingsQuery();
  const issuerName =
    issuerQuery.data?.companyName ?? DEFAULT_ISSUER_COMPANY_NAME;
  const issuerAddress =
    issuerQuery.data?.companyAddress ?? DEFAULT_ISSUER_COMPANY_ADDRESS;
  const issuerContact =
    issuerQuery.data?.companyContact ?? DEFAULT_ISSUER_COMPANY_CONTACT;
  const signatureImageKey = issuerQuery.data?.signatureImageKey ?? null;
  const companyStampKey = issuerQuery.data?.companyStampKey ?? null;
  const companyLogoKey = issuerQuery.data?.companyLogoKey ?? null;
  const bankAccounts = issuerQuery.data?.bankAccounts;
  const bankForDoc = bankAccounts
    ? getIssuerBankAccountForCurrency(doc.currency, bankAccounts)
    : null;
  const showIssuerBank =
    bankForDoc != null && issuerBankAccountHasAnyValue(bankForDoc);
  const invoiceQrKey =
    doc.documentType === "invoice"
      ? (bankForDoc?.qrCodeImageKey?.trim() ?? "")
      : "";
  const showIssuerPaymentBlock = showIssuerBank && bankForDoc != null;

  const docNumber = doc.documentNumber ?? doc.id.slice(0, 8);
  const fmt = (value: number) => formatDocumentCurrency(value, doc.currency);

  // ใบรับเงิน: แสดงยอดเต็มของ invoice แทนยอด proportional ของ receipt
  const displaySubtotal =
    doc.documentType === "receipt" && paymentRecord
      ? paymentRecord.invoiceSubtotal
      : Number(doc.subtotal);
  const displayDiscount =
    doc.documentType === "receipt" && paymentRecord
      ? paymentRecord.invoiceDiscountAmount
      : Number(doc.discountAmount);
  const displayTax =
    doc.documentType === "receipt" && paymentRecord
      ? paymentRecord.invoiceTaxAmount
      : Number(doc.taxAmount);
  const displayTotal =
    doc.documentType === "receipt" && paymentRecord
      ? paymentRecord.invoiceTotal
      : Number(doc.total);
  const displayDepositPercent =
    doc.documentType === "receipt" && doc.invoiceDepositPercent != null
      ? doc.invoiceDepositPercent
      : Number(doc.depositPercent ?? 0);
  const displayDepositAmount =
    Math.round(displayTotal * displayDepositPercent * 100) / 100;

  return (
    <>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 print:grid-cols-2">
        <div className="min-w-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
            {companyLogoKey ? (
              <IssuerDocumentAssetImg
                fileKey={companyLogoKey}
                className="h-[52px] w-auto max-w-[140px] shrink-0 object-contain print:h-12 print:max-w-[120px]"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-primary">{issuerName}</p>
              <p className="mt-1 text-muted-foreground text-sm">
                {issuerAddress}
              </p>
              <p className="text-muted-foreground text-sm">{issuerContact}</p>
            </div>
          </div>
        </div>

        <div className="min-w-0 text-right">
          <p className="font-semibold text-lg text-primary leading-tight">
            {getDocumentTypeLabel(doc.documentType, doc.documentTypeName)}
          </p>
          <p className="mt-2 font-medium">ເລກທີ: #{docNumber}</p>
          {doc.issueDate && (
            <p className="text-muted-foreground text-sm">
              ວັນທີອອກ: {formatDateLocal(doc.issueDate)}
            </p>
          )}
          {doc.dueDate && (
            <p className="text-muted-foreground text-sm">
              ວັນຄວນຊຳລະ: {formatDateLocal(doc.dueDate)}
            </p>
          )}
        </div>
      </div>

      <div className="relative mt-8 grid grid-cols-1 gap-8 border-t pt-8 md:grid-cols-2 print:grid-cols-2">
        <div className="min-w-0">
          <p className="font-semibold text-primary">ລູກຄ້າ (BILLED TO)</p>
          <p className="mt-1 font-medium">{doc.customerName}</p>
          {doc.customerAddress && (
            <p className="text-muted-foreground text-sm">
              {doc.customerAddress}
            </p>
          )}
          {doc.customerContact && (
            <p className="text-muted-foreground text-sm">
              {doc.customerContact}
            </p>
          )}
        </div>

        <div className="min-w-0">
          <p className="font-semibold text-primary">ວິທີຊຳລະ / ເງື່ອນໄຂ</p>
          <p className="mt-1 text-muted-foreground text-sm">
            {doc.paymentTerms ?? "—"}
          </p>
        </div>
      </div>

      <div className="relative mt-8 overflow-x-auto border-t pt-8">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="pb-2 font-medium">ລາຍການ</th>
              <th className="min-w-28 max-w-48 pb-2 font-medium">ລາຍລະອຽດ</th>
              <th className="w-20 pb-2 text-right font-medium">ຈຳນວນ</th>
              <th className="w-28 pb-2 text-right font-medium">ລາຄາຕໍ່ຫນ່ວຍ</th>
              <th className="w-32 pb-2 text-right font-medium">ລວມ</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b">
                <td className="py-3 align-top">{item.description}</td>
                <td className="max-w-48 whitespace-pre-wrap py-3 align-top text-muted-foreground text-xs leading-snug">
                  {item.detail?.trim() ? item.detail : "—"}
                </td>
                <td className="text-right">
                  {item.quantity} {item.unit ?? ""}
                </td>
                <td className="text-right">{fmt(item.unitPrice)}</td>
                <td className="text-right font-medium">
                  {fmt(item.lineTotal)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="relative mt-6 border-border/60 border-t pt-6">
        <div
          className={
            showIssuerPaymentBlock
              ? "flex flex-col gap-6 md:flex-row md:items-start md:justify-between print:flex-row print:items-start print:justify-between"
              : "flex justify-end"
          }
        >
          {showIssuerPaymentBlock && bankForDoc ? (
            <div className="min-w-0 max-w-md shrink">
              <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-start sm:justify-between print:flex-row print:items-start print:justify-between">
                <div className="min-w-0 shrink">
                  <p className="font-medium text-sm">
                    ບັນຊີຮັບເງິນ ({doc.currency})
                  </p>
                  {bankForDoc.accountNumber.trim() ? (
                    <p className="text-muted-foreground text-sm">
                      ເລກບັນຊີ: {bankForDoc.accountNumber}
                    </p>
                  ) : null}
                  {bankForDoc.accountName.trim() ? (
                    <p className="text-muted-foreground text-sm">
                      ຊື່ບັນຊີ: {bankForDoc.accountName}
                    </p>
                  ) : null}
                </div>
                {invoiceQrKey ? (
                  <div className="min-w-0 shrink-0 text-left sm:text-right print:text-right">
                    {/* <p className="font-medium text-sm sm:text-right print:text-right">
                      QR ສຳລັບຊຳລະ
                    </p> */}
                    <div className="mt-1 inline-block">
                      <IssuerDocumentAssetImg
                        fileKey={invoiceQrKey}
                        className="h-32 max-h-40 w-32 max-w-40 object-contain print:h-28 print:w-28"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          <div
            className={
              showIssuerPaymentBlock
                ? "w-full max-w-xs shrink-0 space-y-1 text-sm"
                : "w-full max-w-xs space-y-1 text-sm"
            }
          >
            <div className="flex justify-between">
              <span className="text-muted-foreground">ຍອດລວມ:</span>
              <span>{fmt(displaySubtotal)}</span>
            </div>
            {displayDiscount > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">ສ່ວນລົດ:</span>
                <span>-{fmt(displayDiscount)}</span>
              </div>
            )}
            {displayTax > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">VAT:</span>
                <span>{fmt(displayTax)}</span>
              </div>
            )}
            {displayDepositPercent > 0 && (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    ມັດຈຳ ({Math.round(displayDepositPercent * 10000) / 100}%):
                  </span>
                  <span>{fmt(displayDepositAmount)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between border-t pt-2 font-bold text-primary">
              <span>ລວມທັງໝົດ:</span>
              <span>{fmt(displayTotal)}</span>
            </div>
            {doc.documentType === "receipt" && paymentRecord && (
              <>
                <div className="mt-1 border-t pt-2" />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ວັນທີຊຳລະ:</span>
                  <span>
                    {paymentRecord.paidAt
                      ? formatDateLocal(paymentRecord.paidAt)
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ວິທີຊຳລະ:</span>
                  <span>{paymentMethodLabel(paymentRecord.method)}</span>
                </div>
                <div className="flex justify-between font-semibold text-primary">
                  <span>ຈຳນວນຊຳລະ:</span>
                  <span>{fmt(paymentRecord.amount)}</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span className="text-muted-foreground">ຍອດຄົງເຫຼືອ:</span>
                  <span
                    className={
                      paymentRecord.remaining <= 0 ? "text-emerald-600" : ""
                    }
                  >
                    {fmt(Math.max(0, paymentRecord.remaining))}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {doc.notes && (
        <div className="relative mt-8 border-t pt-6">
          <p className="font-semibold text-primary">ຫມາຍເຫດ</p>
          <p className="mt-1 text-muted-foreground text-sm">{doc.notes}</p>
        </div>
      )}

      {(doc.showSignature ?? true) ? (
        <div className="relative mt-6 border-t pt-6 print:break-inside-avoid">
          <div className="qi-signature-two-col grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:gap-10 print:grid-cols-2 print:gap-4">
            <div className="flex min-h-32 min-w-0 flex-col md:min-h-40">
              <p className="font-semibold text-primary">{doc.customerName}</p>
              <div className="mt-3 min-h-20 flex-1 md:min-h-28" aria-hidden />
            </div>
            <div className="flex min-h-32 min-w-0 flex-col md:min-h-40 md:items-end md:text-right print:items-end print:text-right">
              <p className="w-full font-semibold text-primary md:text-right print:text-right">
                {issuerName}
              </p>
              <div className="mt-3 w-full max-w-sm md:ml-auto print:ml-auto print:max-w-[min(100%,11rem)]">
                {companyStampKey && signatureImageKey ? (
                  <div className="relative flex items-end justify-end">
                    <div className="relative z-0 shrink-0">
                      <IssuerDocumentAssetImg
                        fileKey={companyStampKey}
                        className="h-auto max-h-28 w-auto max-w-[120px] object-contain print:max-h-24 print:max-w-[100px]"
                      />
                    </div>
                    <div className="relative z-10 -ml-6 shrink-0 md:-ml-8">
                      <IssuerDocumentAssetImg
                        fileKey={signatureImageKey}
                        className="h-auto max-h-24 w-auto max-w-[160px] object-contain print:max-h-20 print:max-w-[130px]"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-end justify-end gap-2 md:gap-3">
                    <IssuerDocumentAssetImg
                      fileKey={companyStampKey}
                      className="h-auto max-h-28 w-auto max-w-[120px] object-contain print:max-h-24 print:max-w-[100px]"
                    />
                    <IssuerDocumentAssetImg
                      fileKey={signatureImageKey}
                      className="h-auto max-h-24 w-auto max-w-[160px] object-contain print:max-h-20 print:max-w-[130px]"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {doc.documentType === "receipt" &&
        paymentRecord &&
        paymentRecord.attachments.length > 0 && (
          <div className="relative mt-8 border-t pt-6">
            <p className="mb-3 font-semibold text-primary">ເອກະສານຕິດຂັດ</p>
            <div className="flex flex-wrap gap-4">
              {paymentRecord.attachments.map((a) => (
                <a
                  key={a.id}
                  href={fileUrl(a.fileKey)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-[calc(33.333%-0.75rem)] min-w-[160px] print:w-[30%]"
                >
                  <img
                    src={fileUrl(a.fileKey)}
                    alt={a.fileName}
                    className="h-auto w-full rounded-xl border object-contain shadow-md"
                  />
                </a>
              ))}
            </div>
          </div>
        )}
    </>
  );
}
